import { makeAutoObservable, runInAction } from 'mobx'
import { type IResponseEventCalling } from 'entities/Call/api/types'
import { type CallStore } from 'entities/Call'
import { type Websocket, WebsocketEvent } from 'entities/WebSocket'
import type { UsersStore } from 'entities/Users'

export class ActiveCallingStore {
  private _hasActiveCall = false
  private _activeInboxIdsOnCall: Set<number> = new Set()

  constructor(
    private callStore: CallStore,
    private _usersStore: UsersStore,
    private _websocket: Websocket
  ) {
    makeAutoObservable(this)
    this.initActiveCallsEvent()
  }

  private initActiveCallsEvent = async () => {
    await this._usersStore.initPromise
    this._websocket.subscribe(`calling.${this._usersStore.user_owner_id}.user`)
    this._websocket.on(WebsocketEvent.ClientCalling, this._handleCallingEvent)
  }

  get hasActiveCall() {
    return this._hasActiveCall
  }

  isInboxOnCall = (inboxId: number): boolean => {
    return this._activeInboxIdsOnCall.has(inboxId)
  }

  private _activeCallTimeOutId: NodeJS.Timeout | null = null
  private _inboxCallTimeOutIds: Map<number, NodeJS.Timeout> = new Map()

  private _handleCallingEvent = ({ is_calling, inbox_id }: IResponseEventCalling) => {
    if (this._activeCallTimeOutId) {
      clearTimeout(this._activeCallTimeOutId)
    }
    this._hasActiveCall = is_calling
    if (is_calling) {
      this._activeCallTimeOutId = setTimeout(() => {
        runInAction(() => {
          this._hasActiveCall = false
        })
      }, 10000)
    }

    if (inbox_id === null) return

    const existingTimeout = this._inboxCallTimeOutIds.get(inbox_id)
    if (existingTimeout) clearTimeout(existingTimeout)

    if (!is_calling) {
      this._activeInboxIdsOnCall.delete(inbox_id)
      return
    }

    this._activeInboxIdsOnCall.add(inbox_id)
    const timeoutId = setTimeout(() => {
      runInAction(() => {
        this._activeInboxIdsOnCall.delete(inbox_id)
      })
    }, 10000)
    this._inboxCallTimeOutIds.set(inbox_id, timeoutId)
  }

  private _callingEventTimeOutId: NodeJS.Timeout | null = null
  sendCallingEvent = (value: boolean) => {
    if (this._callingEventTimeOutId) {
      clearTimeout(this._callingEventTimeOutId)
    }
    this._websocket.sendEvent(`calling.${this._usersStore.user_owner_id}.user`, 'client-calling', {
      is_calling: value,
      inbox_id: this.callStore.inboxId,
    })
    if (value) {
      this._callingEventTimeOutId = setTimeout(() => {
        this.sendCallingEvent(this.callStore.startedCall)
      }, 5000)
    }
  }

  private _conversationCallingTimeOutId: NodeJS.Timeout | null = null
  sendConversationCallingEvent = (value: boolean) => {
    if (this._conversationCallingTimeOutId) {
      clearTimeout(this._conversationCallingTimeOutId)
    }
    const inboxId = this.callStore.inboxId
    const conversationId = this.callStore.conversationId
    const user = this._usersStore.user
    if (!inboxId || !conversationId || !user) return
    this._websocket.sendEvent(
      `private-inbox.${inboxId}.conversation`,
      'client-conversation-calling',
      {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        conversation_id: conversationId,
        is_calling: value,
      }
    )
    if (value) {
      this._conversationCallingTimeOutId = setTimeout(() => {
        this.sendConversationCallingEvent(this.callStore.startedCall)
      }, 5000)
    }
  }
}
